import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Modal,
  Paper,
  Typography,
  styled,
} from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { ENTITY } from "constants/entities";
import {
  EXCEL_HEADERS,
  EXCEL_SHEETS,
  BATCH_POLLING_INTERVAL_MS,
  MOVEMENT_TYPES,
  STATUS_CODES,
} from "constants/index";
import {
  ARTICLES_URL,
  ASSOCIATED_CLIENTS_URL,
  MOVEMENT_BATCHES_URL,
  MOVEMENTS_URL,
} from "constants/urls";
import {
  isSameImportValue,
  normalize,
  sanitizeExcelValue,
  serialToDateTime,
  toDateString,
} from "helpers";
import {
  buildImportResultMessage,
  getImportReference,
} from "helpers/fileImports";
import {
  getArticleLocationSuggestionErrorMessage,
  isArticleLocationSuggestionError,
} from "helpers/articleLocationSuggestions";
import { bumpArticleAmountsFromMovements } from "helpers/articles";
import {
  findClientByCode,
  findScopedArticleByClientId,
  getScopedClientIds,
} from "helpers/clientScope";
import { buildAdditionalMovementsPayload } from "helpers/additionalMovements";
import { validateSchema } from "helpers/schemaValidator";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { SessionEntity, useSession } from "providers/SessionProvider";
import { useEffect, useRef, useState } from "react";
import { MdCancel, MdCheck, MdCloudUpload, MdRestartAlt } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  AdditionalItem,
  Article,
  Client,
  Store,
  User,
  Location,
  Movement,
  MovementArticle,
  MovementAdditionalItem,
  MovementAdditionalItemDraft,
  MovementBatch,
  ExcelCellProps,
  FileStructure,
  NewArticle,
  AssociatedClient,
  AssociatedClientMatchItem,
  AssociatedClientDecision,
  ArticleLocationMatchSuggestion,
  ArticleLocationSuggestionError,
} from "types";
import { read, utils } from "xlsx";
import {
  buildAssociatedClientCreatePayload,
  buildAssociatedClientLookupMap,
  buildAssociatedClientMatchItems,
  canCreateAssociatedClients,
  findAssociatedClientInLookupMap,
  getAssociatedClientLookupKey,
  normalizeAssociatedClientName,
} from "helpers/associatedClients";
import AssociatedClientMatchModal from "components/AssociatedClients/AssociatedClientMatchModal";
import ArticleLocationSuggestionPanel from "components/FileImport/ArticleLocationSuggestionPanel";
import GeneratedIncomeMovementsDialog from "components/FileImport/GeneratedIncomeMovementsDialog";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

type RequiredAdditionalImportPreview = {
  additionalDescription: string;
  lineClientName: string;
  movementLabel: string;
};

type GeneratedMovementsReport = {
  movements: Movement[];
  additionalMovements: Movement[];
  clientId?: number;
};

const getMovementImportReference = (movement: Movement, index: number) =>
  getImportReference(movement.numero_remito) ??
  getImportReference(movement.client_document) ??
  `movimiento ${index + 1}`;

export default function FileImport({
  movementType,
  locations,
  clients,
  associatedClients,
  stores,
  users,
  additional_items,
  articles,
}: {
  movementType: string;
  locations: Location[];
  clients: Client[];
  associatedClients: AssociatedClient[];
  stores: Store[];
  users: User[];
  additional_items: AdditionalItem[];
  articles: Article[];
}) {
  const [uploadedFile, setUploadedFile] = useState<FileStructure | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [requiredAdditionalPreview, setRequiredAdditionalPreview] = useState<RequiredAdditionalImportPreview[]>([]);
  const [pendingRequiredAdditionalFile, setPendingRequiredAdditionalFile] = useState<FileStructure | null>(null);
  const [requiredAdditionalModalOpen, setRequiredAdditionalModalOpen] = useState(false);
  const [generatedMovementsReport, setGeneratedMovementsReport] =
    useState<GeneratedMovementsReport | null>(null);
  const [error, setError] = useState(false);
  const [listOfErrors, setListOfErrors] = useState<
    Array<string | ArticleLocationSuggestionError>
  >([]);
  const [resetInput, setResetInput] = useState(Math.random().toString(36));
  const fileUploadRef = useRef(null);
  const { createData: createMovements } = useCrud<FileStructure>(MOVEMENTS_URL);
  const { fetchData: fetchMovementBatch } = useCrud<MovementBatch>(
    MOVEMENT_BATCHES_URL,
    {
      disableAutoFetch: true,
    },
  );
  const navigate = useNavigate();
  const { showSnackBar } = useToast();
  const { userData, addSessionData, updateSessionData } = useSession();
  const { createData: createArticle } = useCrud<NewArticle>(ARTICLES_URL, {
    disableAutoFetch: true,
  });
  const { createData: createAssociatedClient } = useCrud(
    ASSOCIATED_CLIENTS_URL,
    {
      disableAutoFetch: true,
    },
  );
  const batchPollingTimeoutRef = useRef<number | null>(null);
  const pendingBatchPayloadRef = useRef<FileStructure | null>(null);
  const skippedMovementReferencesRef = useRef<string[]>([]);
  const [pendingAssociatedClientFile, setPendingAssociatedClientFile] = useState<FileStructure | null>(null);
  const [failedBatchPayload, setFailedBatchPayload] =
    useState<FileStructure | null>(null);
  const [matchItems, setMatchItems] = useState<AssociatedClientMatchItem[]>([]);
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const shouldValidateAssociatedClients =
    movementType === MOVEMENT_TYPES.INCOME.label ||
    movementType === MOVEMENT_TYPES.DISCHARGE.label;

  async function ensureIncomeVariantsBeforeSubmit(
    file: FileStructure,
    articlesList: Article[],
    createArticle: (payload: NewArticle) => Promise<any>,
  ): Promise<FileStructure> {
    const cache = new Map<string, Article>();
    for (const a of articlesList) cache.set(String(a.code), a);

    const cloned: FileStructure = file.map((mov) => ({
      ...mov,
      articles: mov.articles.map((ma) => ({
        ...ma,
        article_locations: {
          ...ma.article_locations,
          article: { ...ma.article_locations.article },
          location: { ...ma.article_locations.location },
        },
      })),
    }));

    const newVariants: Article[] = [];

    for (const mov of cloned) {
      if (mov.movement_type_id !== MOVEMENT_TYPES.INCOME.id) continue;

      for (const ma of mov.articles) {
        const current = ma.article_locations.article;
        const desiredAPC = Number(ma.amount_per_container ?? 0);
        const currentAPC = Number(current.amount_per_container ?? 0);
        const isStandard = current.is_standard_amount_per_container === true;

        if (!desiredAPC || desiredAPC === currentAPC) continue;

        if (!isStandard) {
          ma.amount_per_container = desiredAPC;
          ma.article_locations.amount_per_container = desiredAPC;
          ma.subtotal = desiredAPC * Number(ma.amount ?? 0);
          ma.subtotal_kg = Number(ma.amount ?? 0) * (current.weight ?? 0);
          continue;
        }

        const variantCode = `${current.code}-${desiredAPC}`;

        let variant = cache.get(variantCode);
        if (!variant) {
          const existing = articlesList.find((a) => a.code === variantCode);
          if (existing) {
            variant = existing;
            cache.set(variantCode, existing);
          }
        }

        if (!variant) {
          const payload: NewArticle = {
            code: variantCode,
            description: `${current.description} (AUTO GENERADO)`,
            amount_per_container: desiredAPC,
            client_id: current.client_id,
            unit_id: current.unit.id,
            container_id: current.container.id,
            weight: current.weight,
            width: current.width ?? null,
            height: current.height ?? null,
            long: current.long ?? null,
            amount: 0,
            units_per_pallet: current.units_per_pallet ?? undefined,
            require_fecha_vencimiento:
              current.require_fecha_vencimiento ?? false,
            is_standard_amount_per_container:
              current.is_standard_amount_per_container ?? false,
            fce: current.fce,
          };

          const resp = await createArticle(payload);
          if (resp.status !== STATUS_CODES.OK) {
            throw new Error(
              `No se pudo crear la variante del artículo ${payload.code}`,
            );
          }
          variant = resp.data as Article;
          cache.set(variantCode, variant);
          newVariants.push(variant);
        }

        ma.article_locations.article = variant;
        ma.article_locations.article_id = variant.id;
        ma.article_id = variant.id;
        ma.client_id = variant.client.id;
        ma.container = variant.container.description;
        ma.description = variant.description;
        ma.weight = variant.weight;
        ma.volume_weight = variant.container?.volume_weight ?? 0;
        ma.unit = variant.unit.description;

        ma.amount_per_container = Number(
          variant.amount_per_container ?? desiredAPC,
        );
        ma.article_locations.amount_per_container = ma.amount_per_container;
        ma.subtotal = ma.amount_per_container * Number(ma.amount ?? 0);
        ma.subtotal_kg = Number(ma.amount ?? 0) * (variant.weight ?? 0);
      }
    }

    if (newVariants.length > 0) {
      for (const v of newVariants) {
        addSessionData(ENTITY.ARTICLES, v as unknown as SessionEntity);
      }
    }

    return cloned;
  }

  const clearBatchPolling = () => {
    if (batchPollingTimeoutRef.current !== null) {
      window.clearTimeout(batchPollingTimeoutRef.current);
      batchPollingTimeoutRef.current = null;
    }
  };

  const getMovementClientId = (movements?: Movement[] | null) =>
    movements?.[0]?.client_id;

  const updateSessionArticlesFromMovements = (movements: Movement[]) => {
    if (!movements.length) {
      return;
    }

    const poolMap = new Map<number, Article>();

    for (const article of articles) {
      poolMap.set(Number(article.id), article);
    }

    for (const movement of movements) {
      for (const movementArticle of movement.articles) {
        const article = movementArticle.article_locations?.article as
          | Article
          | undefined;

        if (article) {
          poolMap.set(Number(article.id), article);
        }
      }
    }

    const upsertArticle = (entity: string, payload: Article) => {
      updateSessionData(entity, payload);
    };

    bumpArticleAmountsFromMovements(
      movements,
      Array.from(poolMap.values()),
      upsertArticle,
      { onPreparation: "subtract", clampNonNegative: true },
    );
  };

  const normalizeBatchErrors = (batch: MovementBatch) => {
    const errors = batch.result?.errors?.filter(Boolean) ?? [];
    const localFailedReferences = skippedMovementReferencesRef.current;

    if (localFailedReferences.length > 0) {
      return [
        buildImportResultMessage(
          "movimientos",
          batch.result?.created_count ?? 0,
          [
            ...localFailedReferences,
            ...(batch.result?.failed_references ?? []),
          ],
        ),
        ...errors,
      ];
    }

    return errors.length > 0
      ? errors
      : [
          batch.result?.message ??
            "Ocurrio un error al procesar los movimientos.",
        ];
  };

  const createAdditionalMovementsForMassiveImport = async (
    basePayload: FileStructure,
    createdMovements: Movement[],
  ): Promise<Movement[]> => {
    const createdMovementByRemito = new Map(
      createdMovements.map((movement) => [
        sanitizeExcelValue(movement.numero_remito),
        movement,
      ]),
    );

    const additionalMovementsPayload = basePayload.flatMap((baseMovement) => {
      const createdMovement = createdMovementByRemito.get(
        sanitizeExcelValue(baseMovement.numero_remito),
      );

      if (!createdMovement?.id || baseMovement.additional_items.length === 0) {
        return [];
      }

      return buildAdditionalMovementsPayload({
        baseMovement,
        headerMovement: baseMovement,
        items: baseMovement.additional_items.map((item) => {
          const draftItem = item as MovementAdditionalItem &
            Partial<MovementAdditionalItemDraft>;
          const baseClientHasLines = clients.some(
            (client) => client.parent_client_id === baseMovement.client_id,
          );
          const lineClientId =
            draftItem.line_client_id ??
            (!baseClientHasLines ? baseMovement.client_id : undefined);

          if (!lineClientId) {
            throw new Error(
              `No se pudo determinar el cliente línea para el adicional "${draftItem.description}" del remito "${baseMovement.numero_remito}".`,
            );
          }

          const lineClientName =
            draftItem.line_client_name ??
            clients.find((client) => client.id === lineClientId)?.razon_social ??
            "";

          return {
            ...draftItem,
            line_client_id: lineClientId,
            line_client_name: lineClientName,
          } as MovementAdditionalItemDraft;
        }),
        originMovementId: createdMovement.id,
      });
    });

    if (additionalMovementsPayload.length === 0) {
      return [];
    }

    const response = await createMovements(additionalMovementsPayload);

    if (response.status !== STATUS_CODES.OK) {
      throw new Error(
        response.data?.message ??
          "Los movimientos base se crearon, pero no se pudieron crear los movimientos adicionales.",
      );
    }

    return response.data as Movement[];
  };

  const showGeneratedIncomeMovements = (
    movements: Movement[],
    additionalMovements: Movement[],
    clientId?: number,
  ) => {
    setGeneratedMovementsReport({
      movements,
      additionalMovements,
      clientId,
    });
  };

  const closeGeneratedIncomeMovements = () => {
    const clientId = generatedMovementsReport?.clientId;

    setGeneratedMovementsReport(null);
    navigate("/operations", { state: { clientId } });
  };

  const handleSuccessfulBatch = async (batch: MovementBatch) => {
    const createdMovements = batch.movements ?? [];
    const pendingBatchPayload = pendingBatchPayloadRef.current;
    let additionalMovements: Movement[] = [];

    if (pendingBatchPayload) {
      try {
        additionalMovements = await createAdditionalMovementsForMassiveImport(
          pendingBatchPayload,
          createdMovements,
        );
      } catch (error: any) {
        clearBatchPolling();
        pendingBatchPayloadRef.current = null;
        setLoading(false);
        showSnackBar(
          error?.message ??
            "El lote masivo se creó, pero no se pudieron crear los movimientos adicionales.",
          "error",
        );
        return;
      }
    }

    const clientId =
      getMovementClientId(createdMovements) ??
      getMovementClientId(pendingBatchPayload);

    updateSessionArticlesFromMovements(createdMovements);
    clearBatchPolling();
    pendingBatchPayloadRef.current = null;
    const localFailedReferences = skippedMovementReferencesRef.current;
    const failedReferences = [
      ...localFailedReferences,
      ...(batch.result?.failed_references ?? []),
    ];
    setLoading(false);
    setUploadedFile(null);
    setFailedBatchPayload(null);
    setError(false);
    setListOfErrors([]);
    setResetInput(Math.random().toString(36));
    skippedMovementReferencesRef.current = [];
    showSnackBar(
      failedReferences.length > 0
        ? buildImportResultMessage(
            "movimientos",
            batch.result?.created_count ?? createdMovements.length,
            failedReferences,
          )
        : (batch.result?.message ?? "Se han creado los movimientos correctamente"),
      failedReferences.length > 0 ? "warning" : "success",
    );
    if (movementType === MOVEMENT_TYPES.INCOME.label) {
      showGeneratedIncomeMovements(
        createdMovements,
        additionalMovements,
        clientId,
      );
    } else {
      navigate("/operations", { state: { clientId } });
    }
  };

  const handleFailedBatch = (batch: MovementBatch) => {
    const failedPayload = pendingBatchPayloadRef.current;

    clearBatchPolling();
    setFailedBatchPayload(failedPayload);
    setLoading(false);
    setUploadedFile(failedPayload);
    setError(true);
    setListOfErrors(normalizeBatchErrors(batch));
    setModalOpen(true);
  };

  const pollMovementBatch = async (batchId: number) => {
    const batchResponse = await fetchMovementBatch(`${batchId}`, true);

    if (batchResponse.status !== STATUS_CODES.OK) {
      clearBatchPolling();
      pendingBatchPayloadRef.current = null;
      setLoading(false);
      showSnackBar(
        batchResponse.data?.message ??
          "No se pudo consultar el estado del lote.",
        "error",
      );
      return;
    }

    const batch = batchResponse.data as MovementBatch;

    if (batch.status === "pending" || batch.status === "processing") {
      batchPollingTimeoutRef.current = window.setTimeout(() => {
        void pollMovementBatch(batchId);
      }, BATCH_POLLING_INTERVAL_MS);
      return;
    }

    if (batch.status === "success") {
      await handleSuccessfulBatch(batch);
      return;
    }

    handleFailedBatch(batch);
  };

  useEffect(() => {
    return () => {
      clearBatchPolling();
    };
  }, []);

  const handleModal = () => {
    setError(false);
    setListOfErrors([]);
    skippedMovementReferencesRef.current = [];
    setModalOpen(!modalOpen);
  };

  const closeErrorState = () => {
    setUploadedFile(null);
    setFailedBatchPayload(null);
    pendingBatchPayloadRef.current = null;
    setResetInput(Math.random().toString(36));
    setError(false);
    setModalOpen(false);
  };

  const applySuggestionToFailedBatch = (
    error: ArticleLocationSuggestionError,
    suggestion: ArticleLocationMatchSuggestion,
  ) => {
    if (!failedBatchPayload) return;

    const movementIndex = error.target?.movement_index;
    const articleIndex = error.target?.article_index;

    if (movementIndex == null || articleIndex == null) {
      setListOfErrors(["No se pudo identificar la linea a reemplazar."]);
      return;
    }

    const suggestionLocation = locations.find(
      (location) => location.id === suggestion.location_id,
    );

    const nextPayload = failedBatchPayload.map((movement, currentMovementIndex) => {
      if (currentMovementIndex !== movementIndex) return movement;

      return {
        ...movement,
        articles: movement.articles.map((article, currentArticleIndex) => {
          if (currentArticleIndex !== articleIndex) return article;

          const nextAmountPerContainer =
            suggestion.amount_per_container ?? article.amount_per_container;
          const nextFechaIngreso =
            toDateString(suggestion.fecha_ingreso) ?? undefined;
          const nextFechaVencimiento =
            toDateString(suggestion.fecha_vencimiento) ?? undefined;

          return {
            ...article,
            article_locations_id: suggestion.article_location_id,
            location_id: suggestion.location_id,
            amount_per_container: nextAmountPerContainer,
            subtotal: Number(article.amount ?? 0) * Number(nextAmountPerContainer ?? 0),
            article_locations: {
              ...article.article_locations,
              id: suggestion.article_location_id,
              location_id: suggestion.location_id,
              location: suggestionLocation ?? article.article_locations.location,
              amount_per_container: nextAmountPerContainer,
              lote: suggestion.lote,
              oc: suggestion.oc,
              fecha_ingreso: nextFechaIngreso,
              fecha_vencimiento: nextFechaVencimiento,
            },
          };
        }),
      };
    });

    setFailedBatchPayload(nextPayload);
    setUploadedFile(nextPayload);
    pendingBatchPayloadRef.current = nextPayload;
    setListOfErrors((previousErrors) =>
      previousErrors.filter((currentError) => currentError !== error),
    );
  };

  const continueWithFailedBatchPayload = () => {
    if (!failedBatchPayload) return;

    const unresolvedMovementIndexes = new Set(
      listOfErrors
        .filter(isArticleLocationSuggestionError)
        .map((error) => error.target?.movement_index)
        .filter((movementIndex): movementIndex is number => movementIndex != null),
    );

    const nextPayload = failedBatchPayload.filter(
      (_, movementIndex) => !unresolvedMovementIndexes.has(movementIndex),
    );
    const skippedReferences = failedBatchPayload
      .map((movement, movementIndex) =>
        unresolvedMovementIndexes.has(movementIndex)
          ? getMovementImportReference(movement, movementIndex)
          : null,
      )
      .filter((reference): reference is string => reference != null);

    if (nextPayload.length === 0) {
      setListOfErrors(["No quedan movimientos validos para importar."]);
      return;
    }

    skippedMovementReferencesRef.current = Array.from(
      new Set([...skippedMovementReferencesRef.current, ...skippedReferences]),
    );

    setUploadedFile(nextPayload);
    setFailedBatchPayload(nextPayload);
    pendingBatchPayloadRef.current = nextPayload;
    setListOfErrors([]);
    setError(false);
  };

  const resolveStoreIdFromExcel = (
    rawStoreValue: string | number | null | undefined,
  ): number | null => {
    const normalized = sanitizeExcelValue(rawStoreValue);

    const normalizedStr = String(normalized).toLowerCase();

    if (!normalizedStr || normalizedStr === "todos") {
      return null;
    }

    const store = stores.find(
      (s) => sanitizeExcelValue(String(s.code)).toLowerCase() === normalizedStr,
    );

    return store?.id ?? null;
  };

  const findArticleByCode = (code: unknown) =>
    articles.find((article) => isSameImportValue(article.code, code));

  const findArticleByClientAndCode = (
    clientCode: unknown,
    code: unknown,
  ) => {
    const client = findClientByCode(clients, clientCode);

    if (!client) {
      return undefined;
    }

    return findScopedArticleByClientId(
      clients,
      articles,
      client.id,
      code,
    );
  };

  const findAdditionalItemByClientAndDescription = (
    clientCode: unknown,
    description: unknown,
  ) => {
    const normalizedDescription = sanitizeExcelValue(
      String(description ?? "").toLowerCase(),
    );
    const client = findClientByCode(clients, clientCode);
    const scopedClientIds = client ? getScopedClientIds(clients, client.id) : [];
    const matchingItems = additional_items.filter(
      (additionalItem) =>
        sanitizeExcelValue(additionalItem.description.toLowerCase()) ===
        normalizedDescription,
    );

    return (
      matchingItems.find(
        (additionalItem) =>
          additionalItem.client_id != null &&
          scopedClientIds.includes(additionalItem.client_id),
      ) ??
      matchingItems.find((additionalItem) => additionalItem.client_id == null)
    );
  };

  const findAdditionalItemPriceForClient = (
    additionalItem: AdditionalItem,
    client: Client,
  ) => {
    const customPrice = client.client_additional_items?.find(
      (item) => item.additional_item_id === additionalItem.id,
    )?.price;

    return customPrice ?? additionalItem.price;
  };

  const findLocationByCode = (code: unknown) =>
    locations.find((location) => isSameImportValue(location.code, code));

  const parseAvailableMerchandiseValue = (
    rawValue: unknown,
  ): boolean => {
    const normalizedValue = normalize(rawValue);

    if (normalizedValue === "si") {
      return true;
    }

    if (normalizedValue === "no") {
      return false;
    }

    throw new Error('Usar "si" o "no" en la columna "Mercaderia Disponible".');
  };

  const applyRequiredAdditionalItemsToImportedMovements = (
    fileStructure: FileStructure,
  ) => {
    const preview: RequiredAdditionalImportPreview[] = [];

    const movementTypeKey =
      movementType === MOVEMENT_TYPES.INCOME.label
        ? "income"
        : movementType === MOVEMENT_TYPES.DISCHARGE.label
          ? "discharge"
          : null;

    if (!movementTypeKey) {
      return { fileStructure, preview };
    }

    const nextFileStructure = fileStructure.map((movement) => {
      const articleClientIds = Array.from(
        new Set(
          movement.articles
            .map((article) => article.client_id)
            .filter((clientId): clientId is number => Boolean(clientId)),
        ),
      );

      const lineClientIds =
        articleClientIds.length > 0 ? articleClientIds : [movement.client_id];

      const nextAdditionalItems = [...movement.additional_items];

      lineClientIds.forEach((lineClientId) => {
        const lineClient = clients.find((client) => client.id === lineClientId);

        if (!lineClient) return;

        const requiredItems = (lineClient.client_additional_items ?? [])
          .filter((item) => item.is_required === true)
          .filter((item) =>
            (item.required_movement_types ?? []).includes(movementTypeKey),
          );

        requiredItems.forEach((requiredItem) => {
          const alreadyExists = nextAdditionalItems.some(
            (item) =>
              item.additional_item_id === requiredItem.additional_item_id &&
              (item as Partial<MovementAdditionalItemDraft>).line_client_id ===
                lineClientId,
          );

          if (alreadyExists) return;

          const additionalItem = additional_items.find(
            (item) => item.id === requiredItem.additional_item_id,
          );

          if (!additionalItem) return;

          nextAdditionalItems.push({
            id: nextAdditionalItems.length,
            additional_item_id: additionalItem.id,
            description: additionalItem.description,
            amount: 1,
            price: Number(findAdditionalItemPriceForClient(additionalItem, lineClient)),
            client_id: additionalItem.client_id,
            line_client_id: lineClient.id,
            line_client_name: lineClient.razon_social,
            additional_item: additionalItem,
          } as MovementAdditionalItemDraft);

          preview.push({
            additionalDescription: additionalItem.description,
            lineClientName: lineClient.razon_social,
            movementLabel: `Remito ${movement.numero_remito || movement.client_document}`,
          });
        });
      });

      return {
        ...movement,
        additional_items: nextAdditionalItems,
      };
    });

    return { fileStructure: nextFileStructure, preview };
  };

  const continueWithAssociatedClientValidation = (structure: FileStructure) => {
    if (shouldValidateAssociatedClients) {
      const sources = structure.map((movement) => {
        const client = clients.find((item) => item.id === movement.client_id);

        return {
          clientId: movement.client_id,
          clientCode: String(client?.code ?? ""),
          associatedClientName: movement.associated_client,
        };
      });

      const nextMatchItems = buildAssociatedClientMatchItems(
        sources,
        associatedClients,
      );

      if (nextMatchItems.length > 0) {
        setPendingAssociatedClientFile(structure);
        setMatchItems(nextMatchItems);
        setModalOpen(false);
        setMatchModalOpen(true);
        return;
      }
    }

    setUploadedFile(structure);
    setModalOpen(true);
  };

  async function createFileStructure({
    movimientos,
    articulos,
    items_adicionales,
  }: {
    movimientos: Record<string, unknown>[];
    articulos: Record<string, unknown>[];
    items_adicionales: Record<string, unknown>[];
  }): Promise<FileStructure> {
    const fileStructure: FileStructure = [];
    if (movementType === MOVEMENT_TYPES.INCOME.label) {
      movimientos.forEach((movimiento) => {
        const client = findClientByCode(
          clients,
          movimiento[EXCEL_HEADERS.CABECERA.CLIENTE],
        );

        const storeId = resolveStoreIdFromExcel(
          movimiento[EXCEL_HEADERS.CABECERA.CODIGO_ALMACEN] as
            | string
            | number
            | null
            | undefined,
        );

        const userCreated = users.find(
          (user) =>
            sanitizeExcelValue(`${user.first_name} ${user.last_name}`) ===
            sanitizeExcelValue(
              movimiento[EXCEL_HEADERS.CABECERA.CREATED_BY] as
                | string
                | number
                | null
                | undefined,
            ),
        );

        const userUpdated = users.find(
          (user) =>
            sanitizeExcelValue(`${user.first_name} ${user.last_name}`) ===
            sanitizeExcelValue(
              movimiento[EXCEL_HEADERS.CABECERA.UPDATED_BY] as
                | string
                | number
                | null
                | undefined,
            ),
        );

        const movimientoStructure: Movement = {
          containers: [],
          movement_type_id: MOVEMENT_TYPES.INCOME.id,
          numero_remito: String(movimiento[EXCEL_HEADERS.CABECERA.REMITO]),
          date: serialToDateTime(
            movimiento[EXCEL_HEADERS.CABECERA.FECHA] as string,
          ) as string,
          client_id: client?.id as number,
          associated_client: movimiento[
            EXCEL_HEADERS.CABECERA.CLIENTE_ASOCIADO
          ] as string,
          client_document: String(
            movimiento[EXCEL_HEADERS.CABECERA.DOCUMENTO_DEL_CLIENTE],
          ),
          pallets: movimiento[EXCEL_HEADERS.CABECERA.PALLETS] as number,
          observation: movimiento[
            EXCEL_HEADERS.CABECERA.OBSERVACIONES
          ] as string,
          created_by: userCreated?.id as number,
          updated_by: userUpdated?.id as number,
          is_billable: false,
          store_id: storeId,
          weight: movimiento[EXCEL_HEADERS.CABECERA.PESO] as number,
          articles: [],
          additional_items: [],
          isMassive: true,
        };

        fileStructure.push(movimientoStructure);
      });

      for (let index = 0; index < articulos.length; index++) {
        const articulo = articulos[index];

        const movimientoDelArticulo = movimientos.find((movimiento) =>
          isSameImportValue(
            movimiento[EXCEL_HEADERS.CABECERA.REMITO],
            articulo[EXCEL_HEADERS.ARTICULOS.REMITO],
          ),
        );

        const base = findArticleByClientAndCode(
          movimientoDelArticulo?.[EXCEL_HEADERS.CABECERA.CLIENTE],
          articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO],
        );

        if (!base) {
          setListOfErrors((prev) => [
            ...prev,
            `Articulo no encontrado para el codigo ${articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO]} del remito ${articulo[EXCEL_HEADERS.ARTICULOS.REMITO]}.`,
          ]);
          setError(true);
          continue;
        }

        if (base.require_fecha_vencimiento === true) {
          const fvRaw = articulo[EXCEL_HEADERS.ARTICULOS.FECHA_VENCIMIENTO];

          if (!fvRaw || String(fvRaw).trim() === "") {
            setListOfErrors((prev) => [
              ...prev,
              `El artículo "${base.code}" requiere fecha de vencimiento y no tiene un valor asignado.`,
            ]);
            setError(true);
            continue;
          }
        }

        const location = findLocationByCode(
          articulo[EXCEL_HEADERS.ARTICULOS.UBICACION],
        );
        if (!location) {
          setListOfErrors((prev) => [
            ...prev,
            `Ubicación no encontrada para el artículo ${articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO]}`,
          ]);
          setError(true);
          continue;
        }

        const qty = Number(articulo[EXCEL_HEADERS.ARTICULOS.CANTIDAD] ?? 0);
        const rawAvailability =
          articulo[EXCEL_HEADERS.ARTICULOS.MERCADERIA_DISPONIBLE];
        let isAvailable = true;

        try {
          isAvailable = parseAvailableMerchandiseValue(rawAvailability);
        } catch (error) {
          setListOfErrors((prev) => [
            ...prev,
            `Estado de mercaderia invalido para el articulo ${articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO]} del remito ${articulo[EXCEL_HEADERS.ARTICULOS.REMITO]}. ${error instanceof Error ? error.message : "Revisar la columna de disponibilidad."}`,
          ]);
          setError(true);
          continue;
        }

        const excelAPC = Number(
          articulo[EXCEL_HEADERS.ARTICULOS.CANTIDAD_POR_CONTENEDOR] ?? 0,
        );

        const baseAPC = Number(base.amount_per_container ?? 0);
        const shouldUseVariant =
          base.is_standard_amount_per_container === true &&
          excelAPC > 0 &&
          excelAPC !== baseAPC;
        const variantCode = `${base.code}-${excelAPC}`;
        const variantFromList =
          shouldUseVariant
            ? (articles || []).find((a) => isSameImportValue(a.code, variantCode))
            : undefined;

        const usedArticle = variantFromList ?? base;

        const amountPerContainerForLine =
          excelAPC > 0
            ? excelAPC
            : Number(usedArticle.amount_per_container ?? 0);
        const articuloStructure: MovementArticle = {
          id: index,
          article_locations: {
            id: 0,
            article_id: usedArticle.id as number,
            location_id: location.id as number,
            article: usedArticle,
            location: location,
            stock: qty,
            reserved: 0,
            not_available: isAvailable ? 0 : qty,
            amount_per_container: amountPerContainerForLine,
            oc: articulo[EXCEL_HEADERS.ARTICULOS.ORDEN_DE_COMPRA] as string,
            lote: articulo[EXCEL_HEADERS.ARTICULOS.LOTE] as string,
            fecha_vencimiento: articulo[
              EXCEL_HEADERS.ARTICULOS.FECHA_VENCIMIENTO
            ]
              ? (serialToDateTime(
                  articulo[EXCEL_HEADERS.ARTICULOS.FECHA_VENCIMIENTO] as string,
                ) as string)
              : undefined,
          },
          article_locations_id: 0,
          article_id: usedArticle.id as number,
          client_id: usedArticle.client.id as number,
          location_id: location.id as number,
          container: usedArticle.container.description as string,
          container_id: usedArticle.container.id,
          unit_id: usedArticle.unit.id,
          description: usedArticle.description as string,
          weight: usedArticle.weight as number,
          volume_weight: usedArticle.container?.volume_weight ?? 0,
          unit: usedArticle.unit.description as string,
          amount: qty,
          amount_per_container: amountPerContainerForLine,
          subtotal: amountPerContainerForLine * qty,
          subtotal_kg: qty * (usedArticle.weight ?? 0),
        };

        fileStructure.forEach((mov) => {
          if (
            sanitizeExcelValue(String(mov.numero_remito)) ===
            sanitizeExcelValue(String(articulo[EXCEL_HEADERS.ARTICULOS.REMITO]))
          ) {
            mov.articles.push(articuloStructure);
          }
        });
      }

      items_adicionales.forEach((item, index) => {
        const descriptionItem =
          (item as Record<string, string>)[
            EXCEL_HEADERS.ADICIONALES.DESCRIPCION
          ] || "";
        const lineClientCode =
          item[EXCEL_HEADERS.ADICIONALES.CODIGO_LINEA];
        const movimientoDelItem = movimientos.find((movimiento) =>
          isSameImportValue(
            movimiento[EXCEL_HEADERS.CABECERA.REMITO],
            item[EXCEL_HEADERS.ADICIONALES.REMITO],
          ),
        );
        const additionalItem = findAdditionalItemByClientAndDescription(
          lineClientCode ||
            movimientoDelItem?.[EXCEL_HEADERS.CABECERA.CLIENTE],
          descriptionItem,
        );

        if (!additionalItem) {
          setListOfErrors((prevState) => [
            ...prevState,
            `Ítem adicional no reconocido en la base de datos: "${descriptionItem}"`,
          ]);
          setError(true);
          return;
        }

        fileStructure.forEach((movimiento) => {
          if (
            sanitizeExcelValue(String(movimiento.numero_remito)) ===
            sanitizeExcelValue(String(item[EXCEL_HEADERS.ADICIONALES.REMITO]))
          ) {
            const articleClientIds = Array.from(
              new Set(
                movimiento.articles
                  .map((article) => article.client_id)
                  .filter((clientId): clientId is number => Boolean(clientId)),
              ),
            );
            const requestedLineClient = lineClientCode
              ? findClientByCode(clients, lineClientCode)
              : undefined;
            const scopedClientIds = getScopedClientIds(
              clients,
              movimiento.client_id,
            );
            const matchingAdditionalLineClientIds = Array.from(
              new Set(
                additional_items
                  .filter(
                    (candidate) =>
                      candidate.client_id != null &&
                      articleClientIds.includes(candidate.client_id) &&
                      sanitizeExcelValue(candidate.description.toLowerCase()) ===
                        sanitizeExcelValue(descriptionItem.toLowerCase()),
                  )
                  .map((candidate) => candidate.client_id as number),
              ),
            );
            const inferredLineClientId =
              requestedLineClient?.id ??
              (articleClientIds.length === 1
                ? articleClientIds[0]
                : matchingAdditionalLineClientIds.length === 1
                  ? matchingAdditionalLineClientIds[0]
                  : undefined);

            if (
              !inferredLineClientId ||
              !scopedClientIds.includes(inferredLineClientId) ||
              (articleClientIds.length > 0 &&
                !articleClientIds.includes(inferredLineClientId))
            ) {
              throw new Error(
                `No se pudo determinar el cliente línea para el adicional "${descriptionItem}" del remito "${movimiento.numero_remito}". Informá la columna "${EXCEL_HEADERS.ADICIONALES.CODIGO_LINEA}".`,
              );
            }

            const lineClient = clients.find(
              (client) => client.id === inferredLineClientId,
            );
            const additionalItemStructure: MovementAdditionalItemDraft = {
              id: index,
              additional_item_id: additionalItem.id,
              description: additionalItem.description,
              additional_item: additionalItem,
              amount: item[EXCEL_HEADERS.ADICIONALES.CANTIDAD] as number,
              line_client_id: inferredLineClientId,
              line_client_name: lineClient?.razon_social ?? "",
            };

            movimiento.additional_items.push(additionalItemStructure);
          }
        });
      });
    } else {
      movimientos.forEach((movimiento) => {
        const client = findClientByCode(
          clients,
          movimiento[EXCEL_HEADERS.CABECERA.CLIENTE],
        );
        const storeId = resolveStoreIdFromExcel(
          movimiento[EXCEL_HEADERS.CABECERA.CODIGO_ALMACEN] as
            | string
            | number
            | null
            | undefined,
        );
        const userCreated = users.find(
          (user) =>
            sanitizeExcelValue(`${user.first_name} ${user.last_name}`) ===
            sanitizeExcelValue(
              movimiento[EXCEL_HEADERS.CABECERA.CREATED_BY] as
                | string
                | number
                | null
                | undefined,
            ),
        );
        const userUpdated = users.find(
          (user) =>
            sanitizeExcelValue(`${user.first_name} ${user.last_name}`) ===
            sanitizeExcelValue(
              movimiento[EXCEL_HEADERS.CABECERA.UPDATED_BY] as
                | string
                | number
                | null
                | undefined,
            ),
        );

        const movimientoStructure: Movement = {
          containers: [],
          movement_type_id:
            movementType === MOVEMENT_TYPES.DISCHARGE.label
              ? MOVEMENT_TYPES.DISCHARGE.id
              : MOVEMENT_TYPES.PREPARATION.id,
          numero_remito: String(movimiento[EXCEL_HEADERS.CABECERA.REMITO]),
          date: serialToDateTime(
            movimiento[EXCEL_HEADERS.CABECERA.FECHA] as string,
          ) as string,
          client_id: client?.id as number,
          associated_client: movimiento[
            EXCEL_HEADERS.CABECERA.CLIENTE_ASOCIADO
          ] as string,
          client_document: String(
            movimiento[EXCEL_HEADERS.CABECERA.DOCUMENTO_DEL_CLIENTE],
          ),
          pallets: movimiento[EXCEL_HEADERS.CABECERA.PALLETS] as number,
          observation: movimiento[
            EXCEL_HEADERS.CABECERA.OBSERVACIONES
          ] as string,
          created_by: userCreated?.id as number,
          updated_by: userUpdated?.id as number,
          is_billable: false,
          store_id: storeId,
          weight: movimiento[EXCEL_HEADERS.CABECERA.PESO] as number,
          articles: [],
          additional_items: [],
          isMassive: true,
        };
        fileStructure.push(movimientoStructure);
      });

      articulos.forEach((articulo, index) => {
        const movimientoDelArticulo = movimientos.find((movimiento) =>
          isSameImportValue(
            movimiento[EXCEL_HEADERS.CABECERA.REMITO],
            articulo[EXCEL_HEADERS.ARTICULOS.REMITO],
          ),
        );

        const article = findArticleByClientAndCode(
          movimientoDelArticulo?.[EXCEL_HEADERS.CABECERA.CLIENTE],
          articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO],
        );

        if (!article) {
          console.error(
            `Artículo con código ${articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO]} no encontrado.`,
          );
          setListOfErrors((prevState) => [
            ...prevState,
            `Artículo no encontrado: ${articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO]}`,
          ]);
          setError(true);
          return;
        }

        const location = findLocationByCode(
          articulo[EXCEL_HEADERS.ARTICULOS.UBICACION],
        );

        if (!location) {
          setListOfErrors((prev) => [
            ...prev,
            `Ubicación no encontrada para el artículo ${articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO]}`,
          ]);
          setError(true);
          return;
        }

        const articuloStructure: MovementArticle = {
          id: index,
          article_locations: {
            id: 0,
            article_id: article?.id as number,
            location_id: location?.id as number,
            article: article as Article,
            location: location as Location,
            stock: articulo[EXCEL_HEADERS.ARTICULOS.CANTIDAD] as number,
            reserved: 0,
            amount_per_container: articulo[
              EXCEL_HEADERS.ARTICULOS.CANTIDAD_POR_CONTENEDOR
            ] as number,
            oc: articulo[EXCEL_HEADERS.ARTICULOS.ORDEN_DE_COMPRA] as string,
            lote: articulo[EXCEL_HEADERS.ARTICULOS.LOTE] as string,
            fecha_vencimiento: articulo[
              EXCEL_HEADERS.ARTICULOS.FECHA_VENCIMIENTO
            ]
              ? (serialToDateTime(
                  articulo[EXCEL_HEADERS.ARTICULOS.FECHA_VENCIMIENTO] as string,
                ) as string)
              : undefined,
            fecha_ingreso: articulo[EXCEL_HEADERS.ARTICULOS.FECHA_INGRESO]
              ? (serialToDateTime(
                  articulo[EXCEL_HEADERS.ARTICULOS.FECHA_INGRESO] as string,
                ) as string)
              : undefined,
          },
          article_locations_id: 0,
          article_id: article?.id as number,
          client_id: article?.client.id as number,
          location_id: location?.id as number,
          container: article?.container.description as string,
          container_id: article?.container.id,
          unit_id: article?.unit.id,
          description: article?.description as string,
          weight: article?.weight as number,
          volume_weight: article?.container?.volume_weight ?? 0,
          unit: article?.unit.description as string,
          amount: articulo[EXCEL_HEADERS.ARTICULOS.CANTIDAD] as number,
          amount_per_container: articulo[
            EXCEL_HEADERS.ARTICULOS.CANTIDAD_POR_CONTENEDOR
          ] as number,
          subtotal:
            Number(articulo[EXCEL_HEADERS.ARTICULOS.SUBTOTAL]) ||
            Number(articulo[EXCEL_HEADERS.ARTICULOS.CANTIDAD] as number) *
              Number(
                articulo[
                  EXCEL_HEADERS.ARTICULOS.CANTIDAD_POR_CONTENEDOR
                ] as number,
              ),
          subtotal_kg:
            Number(articulo[EXCEL_HEADERS.ARTICULOS.CANTIDAD] as number) *
            Number(article.weight ?? 0),
        };

        fileStructure.forEach((movimiento) => {
          if (
            sanitizeExcelValue(String(movimiento.numero_remito)) ===
            sanitizeExcelValue(String(articulo[EXCEL_HEADERS.ARTICULOS.REMITO]))
          ) {
            movimiento.articles.push(articuloStructure);
          }
        });
      });

      items_adicionales.forEach((item, index) => {
        const descriptionItem = String(
          item[EXCEL_HEADERS.ADICIONALES.DESCRIPCION] ?? "",
        );
        const lineClientCode =
          item[EXCEL_HEADERS.ADICIONALES.CODIGO_LINEA];
        const movimientoDelItem = movimientos.find((movimiento) =>
          isSameImportValue(
            movimiento[EXCEL_HEADERS.CABECERA.REMITO],
            item[EXCEL_HEADERS.ADICIONALES.REMITO],
          ),
        );
        const additionalItem = findAdditionalItemByClientAndDescription(
          lineClientCode ||
            movimientoDelItem?.[EXCEL_HEADERS.CABECERA.CLIENTE],
          descriptionItem,
        );

        if (!additionalItem) {
          setListOfErrors((prevState) => [
            ...prevState,
            `Ítem adicional no reconocido en la base de datos: "${descriptionItem}"`,
          ]);
          setError(true);
          return;
        }

        fileStructure.forEach((movimiento) => {
          if (
            sanitizeExcelValue(String(movimiento.numero_remito)) ===
            sanitizeExcelValue(String(item[EXCEL_HEADERS.ADICIONALES.REMITO]))
          ) {
            const articleClientIds = Array.from(
              new Set(
                movimiento.articles
                  .map((article) => article.client_id)
                  .filter((clientId): clientId is number => Boolean(clientId)),
              ),
            );
            const requestedLineClient = lineClientCode
              ? findClientByCode(clients, lineClientCode)
              : undefined;
            const scopedClientIds = getScopedClientIds(
              clients,
              movimiento.client_id,
            );
            const matchingAdditionalLineClientIds = Array.from(
              new Set(
                additional_items
                  .filter(
                    (candidate) =>
                      candidate.client_id != null &&
                      articleClientIds.includes(candidate.client_id) &&
                      sanitizeExcelValue(candidate.description.toLowerCase()) ===
                        sanitizeExcelValue(descriptionItem.toLowerCase()),
                  )
                  .map((candidate) => candidate.client_id as number),
              ),
            );
            const inferredLineClientId =
              requestedLineClient?.id ??
              (articleClientIds.length === 1
                ? articleClientIds[0]
                : matchingAdditionalLineClientIds.length === 1
                  ? matchingAdditionalLineClientIds[0]
                  : undefined);

            if (
              !inferredLineClientId ||
              !scopedClientIds.includes(inferredLineClientId) ||
              (articleClientIds.length > 0 &&
                !articleClientIds.includes(inferredLineClientId))
            ) {
              throw new Error(
                `No se pudo determinar el cliente línea para el adicional "${descriptionItem}" del remito "${movimiento.numero_remito}". Informá la columna "${EXCEL_HEADERS.ADICIONALES.CODIGO_LINEA}".`,
              );
            }

            const lineClient = clients.find(
              (client) => client.id === inferredLineClientId,
            );
            const additionalItemStructure: MovementAdditionalItemDraft = {
              id: index,
              additional_item_id: additionalItem.id,
              description: additionalItem.description,
              additional_item: additionalItem,
              amount: item[EXCEL_HEADERS.ADICIONALES.CANTIDAD] as number,
              line_client_id: inferredLineClientId,
              line_client_name: lineClient?.razon_social ?? "",
            };

            movimiento.additional_items.push(additionalItemStructure);
          }
        });
      });
    }
    return fileStructure;
  }

  const handleConfirmAssociatedClientMatches = (
    decisions: AssociatedClientDecision[],
  ) => {
    if (!pendingAssociatedClientFile) return;

    const decisionMap = new Map(
      decisions.map((decision) => [decision.key, decision] as const),
    );

    const resolvedFile = pendingAssociatedClientFile.map((movement) => {
      const key = getAssociatedClientLookupKey(
        movement.client_id,
        movement.associated_client,
      );

      const decision = key ? decisionMap.get(key) : undefined;

      if (
        decision?.action !== "use-existing" ||
        decision.associatedClientId == null
      ) {
        return { ...movement };
      }

      const selectedAssociatedClient = associatedClients.find(
        (associatedClient) =>
          associatedClient.id === decision.associatedClientId,
      );

      return {
        ...movement,
        associated_client:
          selectedAssociatedClient?.razon_social ??
          movement.associated_client,
      };
    });

    setUploadedFile(resolvedFile);
    setPendingAssociatedClientFile(null);
    setMatchItems([]);
    setMatchModalOpen(false);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setModalOpen(false);
      setLoading(true);
      if (!uploadedFile || uploadedFile.length === 0) {
        setLoading(false);
        showSnackBar("No hay datos para cargar.", "error");
        return;
      }

      const fileToSubmit = uploadedFile.map((movement) => ({
        ...movement,
      }));

      if (shouldValidateAssociatedClients) {
        const associatedClientMap =
          buildAssociatedClientLookupMap(associatedClients);

        for (const movement of fileToSubmit) {
          const associatedClientName = normalizeAssociatedClientName(
            movement.associated_client,
          );

          if (!associatedClientName) continue;

          const existingAssociatedClient =
            findAssociatedClientInLookupMap(
              associatedClientMap,
              movement.client_id,
              associatedClientName,
            );

          if (existingAssociatedClient) {
            movement.associated_client =
              existingAssociatedClient.razon_social;
            continue;
          }

          if (!canCreateAssociatedClients(userData?.role)) {
            throw new Error(
              `Tu rol no permite crear el cliente asociado "${associatedClientName}".`,
            );
          }

          const response = await createAssociatedClient(
            buildAssociatedClientCreatePayload(
              movement.client_id,
              associatedClientName,
            ),
            true,
          );

          if (response.status !== STATUS_CODES.OK) {
            throw new Error(
              response.data?.message ??
                `No se pudo crear el cliente asociado "${associatedClientName}".`,
            );
          }

          const createdAssociatedClient =
            response.data as AssociatedClient;

          addSessionData(
            ENTITY.ASSOCIATED_CLIENTS,
            createdAssociatedClient,
          );

          const createdKey = getAssociatedClientLookupKey(
            createdAssociatedClient.client_id,
            createdAssociatedClient.razon_social,
          );

          if (createdKey) {
            associatedClientMap.set(
              createdKey,
              createdAssociatedClient,
            );
          }

          movement.associated_client =
            createdAssociatedClient.razon_social;
        }
      }

      const prepared = await ensureIncomeVariantsBeforeSubmit(
        fileToSubmit,
        articles,
        createArticle,
      );

      const baseMovementsPayload = prepared.map((movement) => ({
        ...movement,
        additional_items: [],
      }));

      const movementResponse = await createMovements(baseMovementsPayload);

      if (movementResponse.status === STATUS_CODES.ACCEPTED) {
        const batchId = Number(movementResponse.data?.batchId);

        if (!Number.isFinite(batchId)) {
          setLoading(false);
          showSnackBar(
            "La API no devolvio un identificador de lote valido.",
            "error",
          );
          return;
        }

        pendingBatchPayloadRef.current = prepared;
        showSnackBar(
          movementResponse.data?.message ??
            "Lote masivo recibido y en procesamiento.",
          "info",
        );
        void pollMovementBatch(batchId);
        return;
      }

      if (movementResponse.status === STATUS_CODES.OK) {
        const createdMovements = movementResponse.data as Movement[];
        const additionalMovements = await createAdditionalMovementsForMassiveImport(
          prepared,
          createdMovements,
        );

        updateSessionArticlesFromMovements(prepared);

        const clientId = getMovementClientId(prepared);
        const localFailedReferences = skippedMovementReferencesRef.current;
        setLoading(false);
        setUploadedFile(null);
        setResetInput(Math.random().toString(36));
        skippedMovementReferencesRef.current = [];
        showSnackBar(
          localFailedReferences.length > 0
            ? buildImportResultMessage(
                "movimientos",
                createdMovements.length,
                localFailedReferences,
              )
            : "Se han creado los movimientos correctamente",
          localFailedReferences.length > 0 ? "warning" : "success",
        );
        if (movementType === MOVEMENT_TYPES.INCOME.label) {
          showGeneratedIncomeMovements(
            createdMovements,
            additionalMovements,
            clientId,
          );
        } else {
          navigate("/operations", { state: { clientId } });
        }
      } else {
        setLoading(false);
        const errorMessage =
          movementResponse.status === STATUS_CODES.BAD_REQUEST
            ? "Ocurrio un error al procesar los movimientos."
            : (movementResponse.data?.message ??
              "Ocurrio un error al procesar los movimientos.");
        showSnackBar(errorMessage, "error");
      }
    } catch (error: any) {
      console.error("Error al crear movimientos:", error);
      clearBatchPolling();
      pendingBatchPayloadRef.current = null;
      setLoading(false);
      showSnackBar(
        error?.message ?? "Ocurrio un error inesperado. Intente nuevamente.",
        "error",
      );
    }
  };

  const inputMimeType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";

  const readFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    setError(false);
    setListOfErrors([]);
    e.preventDefault();

    try {
      const file = e.target.files?.[0];
      if (!file) {
        setError(true);
        setLoading(false);
        return;
      }

      handleModal();

      const reader = new FileReader();

      reader.onerror = () => {
        setError(true);
        setListOfErrors((prev) => [...prev, "No se pudo leer el archivo."]);
        setLoading(false);
      };

      reader.onload = async (ev) => {
        try {
          // Procesar workbook
          const workbook = read(
            (ev.target as FileReader).result as ArrayBuffer,
            { type: "array" },
          );

          // Validación de nombres de hojas (con tu lógica actual)
          if (
            !workbook.Sheets[workbook.SheetNames[0]] ||
            workbook.SheetNames[0] !== EXCEL_SHEETS.CABECERA ||
            !workbook.Sheets[workbook.SheetNames[1]] ||
            workbook.SheetNames[1] !== EXCEL_SHEETS.ARTICULOS ||
            !workbook.Sheets[workbook.SheetNames[2]] ||
            workbook.SheetNames[2] !== EXCEL_SHEETS.ADICIONALES
          ) {
            setError(true);
          }

          // Parseo de hojas
          const cabecera = utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[0]],
          ) as Record<string, unknown>[];

          const articulos = utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[1]],
          ) as Record<string, unknown>[];

          const itemsAdicionales = utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[2]],
          ) as Record<string, unknown>[];

          // ---- Tus validaciones actuales (sin cambios) ----
          const remitoClienteMap = new Set<string>();
          cabecera.forEach((movimiento) => {
            const rawRemito = sanitizeExcelValue(
              (movimiento[EXCEL_HEADERS.CABECERA.REMITO] ?? "")
                .toString()
                .trim(),
            );
            const rawCliente = sanitizeExcelValue(
              (movimiento[EXCEL_HEADERS.CABECERA.CLIENTE] ?? "")
                .toString()
                .trim(),
            );
            const key = `${rawCliente.toLowerCase()}::${rawRemito.toLowerCase()}`;
            if (remitoClienteMap.has(key)) {
              const errorMsg = `Duplicado detectado: El cliente "${rawCliente}" tiene múltiples movimientos con el mismo número de remito "${rawRemito || "(vacío)"}".`;
              setListOfErrors((prev) => [...prev, errorMsg]);
              setError(true);
            } else {
              remitoClienteMap.add(key);
            }
          });

          const uniqueMovements = new Set(
            cabecera.map(
              (movimiento) => movimiento[EXCEL_HEADERS.CABECERA.REMITO],
            ),
          );

          const uniqueArticles = new Set(
            articulos.map((articulo) => ({
              movement: articulo[EXCEL_HEADERS.ARTICULOS.REMITO],
              article: articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO],
            })),
          );

          const uniqueAdditionalItems = new Set(
            itemsAdicionales.map((item) => ({
              movement: item[EXCEL_HEADERS.ADICIONALES.REMITO],
              item: item[EXCEL_HEADERS.ADICIONALES.DESCRIPCION],
            })),
          );

          if (
            !cabecera.length ||
            (!articulos.length && !itemsAdicionales.length) ||
            uniqueMovements.size !== cabecera.length ||
            uniqueArticles.size !== articulos.length ||
            uniqueAdditionalItems.size !== itemsAdicionales.length
          ) {
            setError(true);
          }

          // Validación de pertenencia cliente-artículo / cliente-ítem (tu lógica)
          cabecera.forEach((movimiento) => {
            const articulosDelMovimiento = articulos.filter(
              (articulo) =>
                sanitizeExcelValue(
                  String(articulo[EXCEL_HEADERS.ARTICULOS.REMITO]),
                ) ===
                sanitizeExcelValue(
                  String(movimiento[EXCEL_HEADERS.CABECERA.REMITO]),
                ),
            );

            const itemsAdicionalesDelMovimiento = itemsAdicionales.filter(
              (item) =>
                sanitizeExcelValue(
                  String(item[EXCEL_HEADERS.ADICIONALES.REMITO]),
                ) ===
                sanitizeExcelValue(
                  String(movimiento[EXCEL_HEADERS.CABECERA.REMITO]),
                ),
            );

            articulosDelMovimiento.forEach((articulo) => {
              const codeClientMovement = sanitizeExcelValue(
                String(movimiento[EXCEL_HEADERS.CABECERA.CLIENTE]),
              );

              const articuloEncontrado = findArticleByClientAndCode(
                codeClientMovement,
                articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO],
              );

              if (!articuloEncontrado) {
                const articuloDeOtroCliente = findArticleByCode(
                  articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO],
                );

                if (!articuloDeOtroCliente) return;

                const codeClientArticle = sanitizeExcelValue(
                  String(articuloDeOtroCliente.client.code),
                );

                const errorMsg = `El artículo con código "${articulo[EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO]}" pertenece al cliente "${codeClientArticle}", pero en el remito "${movimiento[EXCEL_HEADERS.CABECERA.REMITO]}" se indicó el cliente "${codeClientMovement}"`;

                setListOfErrors((prev) =>
                  prev.includes(errorMsg) ? prev : [...prev, errorMsg],
                );
                setError(true);
              }
            });

            itemsAdicionalesDelMovimiento.forEach((item) => {
              const descriptionItem =
                (item as Record<string, string>)[
                  EXCEL_HEADERS.ADICIONALES.DESCRIPCION
                ] || "";
              const lineClientCode =
                item[EXCEL_HEADERS.ADICIONALES.CODIGO_LINEA];
              const itemEncontrado = findAdditionalItemByClientAndDescription(
                lineClientCode ||
                  movimiento[EXCEL_HEADERS.CABECERA.CLIENTE],
                descriptionItem,
              );

              if (!itemEncontrado) {
                setListOfErrors((prevState) => [
                  ...prevState,
                  `Ítem adicional no encontrado: ${descriptionItem}`,
                ]);
                setError(true);
                return;
              }

              const codeClientMovement = String(
                sanitizeExcelValue(
                  movimiento[EXCEL_HEADERS.CABECERA.CLIENTE] as
                    | string
                    | number
                    | null
                    | undefined,
                ),
              );

              const movementClient = findClientByCode(
                clients,
                movimiento[EXCEL_HEADERS.CABECERA.CLIENTE],
              );
              const scopedClientIds = movementClient
                ? getScopedClientIds(clients, movementClient.id)
                : [];
              const codeClientItem = String(
                sanitizeExcelValue(itemEncontrado.client?.code),
              );

              if (
                itemEncontrado.client_id !== null &&
                !scopedClientIds.includes(itemEncontrado.client_id)
              ) {
                setListOfErrors((prevState) => [
                  ...prevState,
                  `Código de cliente incorrecto para el ítem adicional "${descriptionItem}". Se esperaba "${codeClientItem}" pero se encontró "${codeClientMovement}".`,
                ]);
                setError(true);
              }
            });
          });

          const cabeceraSchema: Record<string, ExcelCellProps> = {
            [EXCEL_HEADERS.CABECERA.REMITO]: { type: "string", required: true },
            [EXCEL_HEADERS.CABECERA.FECHA]: { type: "string", required: true },
            [EXCEL_HEADERS.CABECERA.CLIENTE]: {
              type: "string",
              required: true,
              associations: clients.map((client) => client.code),
            },
            [EXCEL_HEADERS.CABECERA.CLIENTE_ASOCIADO]: {
              type: "string",
              required: true,
            }, //TODO: falta asociacion
            [EXCEL_HEADERS.CABECERA.DOCUMENTO_DEL_CLIENTE]: {
              type: "string",
              required: true,
            },
            [EXCEL_HEADERS.CABECERA.CREATED_BY]: {
              type: "string",
              required: true,
              associations: users.map(
                (user) => `${user.first_name} ${user.last_name}`,
              ),
            },
            [EXCEL_HEADERS.CABECERA.UPDATED_BY]: {
              type: "string",
              required: true,
              associations: users.map(
                (user) => `${user.first_name} ${user.last_name}`,
              ),
            },
            [EXCEL_HEADERS.CABECERA.CODIGO_ALMACEN]: {
              type: "string",
              required: false,
              associations: [...stores.map((store) => store.code), "Todos"],
            },
            [EXCEL_HEADERS.CABECERA.OBSERVACIONES]: {
              type: "string",
              required: false,
            },
            [EXCEL_HEADERS.CABECERA.PALLETS]: {
              type: "number",
              required: true,
            },
            [EXCEL_HEADERS.CABECERA.PESO]: { type: "number", required: true },
          };

          const articulosSchema: Record<string, ExcelCellProps> = {
            [EXCEL_HEADERS.ARTICULOS.REMITO]: {
              type: "string",
              required: true,
            },
            [EXCEL_HEADERS.ARTICULOS.CODIGO_ARTICULO]: {
              type: "string",
              required: true,
              associations: articles.map((article) => article.code),
            },
            [EXCEL_HEADERS.ARTICULOS.CANTIDAD]: {
              type: "number",
              required: true,
            },
            ...(movementType === MOVEMENT_TYPES.INCOME.label
              ? {
                  [EXCEL_HEADERS.ARTICULOS.MERCADERIA_DISPONIBLE]: {
                    type: "string",
                    required: true,
                  },
                }
              : {}),
            [EXCEL_HEADERS.ARTICULOS.LOTE]: { type: "string", required: false },
            [EXCEL_HEADERS.ARTICULOS.FECHA_VENCIMIENTO]: {
              type: "string",
              required: false,
            },
            [EXCEL_HEADERS.ARTICULOS.FECHA_INGRESO]: {
              type: "string",
              required: false,
            },
            [EXCEL_HEADERS.ARTICULOS.ORDEN_DE_COMPRA]: {
              type: "string",
              required: false,
            },
            [EXCEL_HEADERS.ARTICULOS.CANTIDAD_POR_CONTENEDOR]: {
              type: "number",
              required: true,
            },
            [EXCEL_HEADERS.ARTICULOS.UBICACION]: {
              type: "string",
              required: true,
              associations: locations.map((location) => location.code),
            },
            [EXCEL_HEADERS.ARTICULOS.SUBTOTAL]: {
              type: "number",
              required: false,
            },
          };

          const itemsAdicionalesSchema: Record<string, ExcelCellProps> = {
            [EXCEL_HEADERS.ADICIONALES.REMITO]: {
              type: "string",
              required: true,
            },
            [EXCEL_HEADERS.ADICIONALES.DESCRIPCION]: {
              type: "string",
              required: true,
            }, //TODO: ver si habria que asociar contra additional_items
            [EXCEL_HEADERS.ADICIONALES.CANTIDAD]: {
              type: "number",
              required: true,
            },
            [EXCEL_HEADERS.ADICIONALES.CODIGO_LINEA]: {
              type: "string",
              required: false,
              associations: clients.map((client) => client.code),
            },
          };

          const isCabeceraValid = validateSchema(cabecera, cabeceraSchema);
          const areArticulosValid = validateSchema(articulos, articulosSchema);
          const areItemsAdicionalesValid = validateSchema(
            itemsAdicionales,
            itemsAdicionalesSchema,
          );

          if (
            !isCabeceraValid.isValid ||
            !areArticulosValid.isValid ||
            !areItemsAdicionalesValid.isValid
          ) {
            for (const error of isCabeceraValid.errors) {
              setListOfErrors((prev) => [
                ...prev,
                `${EXCEL_SHEETS.CABECERA}: ${error.message}`,
              ]);
            }
            for (const error of areArticulosValid.errors) {
              setListOfErrors((prev) => [
                ...prev,
                `${EXCEL_SHEETS.ARTICULOS}: ${error.message}`,
              ]);
            }
            for (const error of areItemsAdicionalesValid.errors) {
              setListOfErrors((prev) => [
                ...prev,
                `${EXCEL_SHEETS.ADICIONALES}: ${error.message}`,
              ]);
            }
            setError(true);
            return;
          }

          const structure = await createFileStructure({
            movimientos: cabecera,
            articulos,
            items_adicionales: itemsAdicionales,
          });

          const requiredAdditionalResult = applyRequiredAdditionalItemsToImportedMovements(structure);

          if (requiredAdditionalResult.preview.length > 0) {
            setPendingRequiredAdditionalFile(requiredAdditionalResult.fileStructure);
            setRequiredAdditionalPreview(requiredAdditionalResult.preview);
            setModalOpen(false);
            setRequiredAdditionalModalOpen(true);
            return;
          }

          continueWithAssociatedClientValidation(structure);
        } catch (ex: any) {
          setError(true);
          setListOfErrors((prev) => [
            ...prev,
            `Error procesando el Excel: ${ex.message ?? ex}`,
          ]);
        } finally {
          setLoading(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (ex: any) {
      setError(true);
      setListOfErrors((prev) => [...prev, ex.message ?? String(ex)]);
      setLoading(false);
    }
  };

  const hasRecoverableBatchErrors =
    failedBatchPayload != null &&
    (listOfErrors.length === 0 ||
      listOfErrors.some(isArticleLocationSuggestionError));

  return (
    <>
      <Button
        component="label"
        variant="contained"
        startIcon={<MdCloudUpload />}
      >
        {loading ? <CircularProgress /> : "Cargar Archivo"}
        <VisuallyHiddenInput
          ref={fileUploadRef}
          key={resetInput}
          onChange={readFile}
          accept={inputMimeType}
          type="file"
        />
      </Button>
      <Modal
        open={modalOpen}
        onClose={() => {
          handleModal();
          setUploadedFile(null);
          setResetInput(Math.random().toString(36));
        }}
      >
        <Paper
          sx={{
            position: "fixed",
            left: "50%",
            top: "50%",
            maxHeight: "90vh",
            width: "auto",
            transform: "translate(-50%, -50%)",
            padding: "1rem",
            overflowY: "auto",
          }}
        >
          <Grid container>
            {!error && uploadedFile && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    Datos Importados Correctamente
                  </Typography>{" "}
                </Grid>
                <Grid item xs={12} py={4}>
                  <Typography variant="body1">
                    Se importaron{" "}
                    <Typography color={"green"}>
                      ({uploadedFile.length})
                    </Typography>{" "}
                    movimientos
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                >
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleModal();
                      setUploadedFile(null);
                      setFailedBatchPayload(null);
                      setResetInput(Math.random().toString(36));
                    }}
                    startIcon={<MdCancel />}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    startIcon={<MdCheck />}
                  >
                    Cargar Datos
                  </Button>
                </Grid>
              </>
            )}
            {error && (
              <Grid container justifyContent="center" rowGap={2}>
                <Grid item xs={12}>
                  <Typography padding={2} variant="h5">
                    Ocurrio un Error
                  </Typography>
                </Grid>
                <Grid item xs={12} rowGap={5}>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ArrowDropDownIcon />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      Se encontraron{" "}
                      <Typography color={"red"}>
                        ({listOfErrors.length})
                      </Typography>{" "}
                      errores
                    </AccordionSummary>
                    <AccordionDetails sx={{ maxHeight: "65vh", overflowY: "auto" }}>
                      <Grid container>
                        {listOfErrors.map((error, index) => (
                          <Grid item xs={12} key={index}>
                            <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
                              {getArticleLocationSuggestionErrorMessage(error)}
                            </Alert>
                            {isArticleLocationSuggestionError(error) && (
                              <ArticleLocationSuggestionPanel
                                error={error}
                                title={`Stock sugerido para ${
                                  error.target?.numero_remito
                                    ? `remito ${error.target.numero_remito}`
                                    : error.target?.client_document
                                      ? `documento ${error.target.client_document}`
                                      : "la linea seleccionada"
                                }`}
                                onUseSuggestion={applySuggestionToFailedBatch}
                              />
                            )}
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="contained"
                    onClick={closeErrorState}
                    startIcon={<MdCancel />}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => {
                      if (hasRecoverableBatchErrors) {
                        continueWithFailedBatchPayload();
                        return;
                      }

                      closeErrorState();
                    }}
                    startIcon={
                      hasRecoverableBatchErrors ? <MdCheck /> : <MdRestartAlt />
                    }
                    variant="contained"
                  >
                    {hasRecoverableBatchErrors ? "Siguiente" : "Reintentar"}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Modal>
      <Dialog
        open={requiredAdditionalModalOpen}
        onClose={() => {
          setRequiredAdditionalModalOpen(false);
          setPendingRequiredAdditionalFile(null);
          setRequiredAdditionalPreview([]);
          setUploadedFile(null);
          setResetInput(Math.random().toString(36));
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Adicionales obligatorios</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Se aplicarán {requiredAdditionalPreview.length} adicionales obligatorios automáticamente.
          </Typography>
          <Box
            sx={{
              maxHeight: 280,
              overflowY: "auto",
              mt: 1,
              pr: 1,
            }}
          >
            <List dense>
              {requiredAdditionalPreview.map((item, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={`${item.additionalDescription} - ${item.lineClientName} - ${item.movementLabel}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setRequiredAdditionalModalOpen(false);
              setPendingRequiredAdditionalFile(null);
              setRequiredAdditionalPreview([]);
              setUploadedFile(null);
              setResetInput(Math.random().toString(36));
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!pendingRequiredAdditionalFile) return;
              const fileWithRequiredAdditionals = pendingRequiredAdditionalFile;
              setPendingRequiredAdditionalFile(null);
              setRequiredAdditionalPreview([]);
              setRequiredAdditionalModalOpen(false);
              continueWithAssociatedClientValidation(fileWithRequiredAdditionals);
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
      <AssociatedClientMatchModal
        open={matchModalOpen}
        items={matchItems}
        canCreate={canCreateAssociatedClients(userData?.role)}
        onCancel={() => {
          setMatchModalOpen(false);
          setPendingAssociatedClientFile(null);
          setMatchItems([]);
          setResetInput(Math.random().toString(36));
        }}
        onConfirm={handleConfirmAssociatedClientMatches}
      />
      <GeneratedIncomeMovementsDialog
        open={generatedMovementsReport !== null}
        movements={generatedMovementsReport?.movements ?? []}
        additionalMovements={
          generatedMovementsReport?.additionalMovements ?? []
        }
        clients={clients}
        stores={stores}
        users={users}
        onClose={closeGeneratedIncomeMovements}
      />
      <Backdrop
        open={loading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          color: "#fff",
          backdropFilter: "blur(2px)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
